import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tick02Icon } from "@hugeicons/core-free-icons";
import {
  formatPrice,
  priceAfterAward,
  type PassPlan,
} from "@/components/pricing/planContent";

/** Radio-style plan chooser feeding a single CTA. Shared by the paywall and /pricing. */
export function PlanSelector({
  plans,
  selected,
  onSelect,
  awardPercent,
  className,
}: {
  plans: PassPlan[];
  selected: string;
  onSelect: (planId: string) => void;
  awardPercent?: number | null;
  className?: string;
}) {
  return (
    <div
      className={cn("flex flex-col gap-3", className)}
      role="radiogroup"
      aria-label="Billing plans"
    >
      {plans.map((p) => {
        const isSel = p.id === selected;
        const original = p.originalAmount ?? (awardPercent ? p.amount : null);
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={isSel}
            onClick={() => onSelect(p.id)}
            className={cn(
              "w-full rounded-xl border p-4 text-left transition-colors",
              isSel
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border hover:border-primary/40",
            )}
          >
            <span className="flex items-center gap-3">
              <span
                className={cn(
                  "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border",
                  isSel
                    ? "border-primary bg-primary text-white"
                    : "border-muted-foreground/40",
                )}
              >
                {isSel ? (
                  <HugeiconsIcon
                    icon={Tick02Icon}
                    size={14}
                    strokeWidth={2.5}
                  />
                ) : null}
              </span>

              <span className="flex-1">
                {p.tag ? (
                  <span className="mb-1.5 inline-block rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    {p.tag}
                  </span>
                ) : null}
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{p.name}</span>
                  {p.id === "pass_lifetime" &&
                  p.originalAmount &&
                  p.originalAmount > p.amount ? (
                    <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                      Save{" "}
                      {formatPrice(Math.round(p.originalAmount - p.amount))}
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {p.note}
                </span>
              </span>

              <span className="text-right">
                {original ? (
                  <span className="block text-sm text-muted-foreground line-through decoration-2">
                    {formatPrice(original)}
                  </span>
                ) : null}
                <span
                  className={cn(
                    "block font-bold leading-none",
                    p.originalAmount ? "text-3xl text-primary" : "text-lg",
                  )}
                >
                  {formatPrice(priceAfterAward(p.amount, awardPercent))}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {p.cadence}
                </span>
              </span>
            </span>
            {p.valueNote ? (
              <span className="mt-2.5 block border-t border-primary/15 pt-2 text-xs font-medium text-primary">
                {p.valueNote}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
